import * as Yup from 'yup';

import { onlyNumbers } from 'shared/utils/formatters';
import { isValidCPF, isValidCNPJ } from 'shared/utils/validation';
import { CBO } from 'features/tuss/cbo/types';
import {
  isNotTodayOrFuture,
  isOver18,
  isValidDate,
} from 'features/checkout/utils/dateUtils';
import {
  maxLimitMessage,
  validationAddressForm,
} from 'features/checkout/components/ModalAddressForm/validation';
import { PERSON_KIND } from './constants';

const required = 'Campo obrigatório';

export const professionalValidationSchema = Yup.object().shape({
  cpf: Yup.string()
    .required(required)
    .test(
      'isValidCPF',
      'CPF inválido',
      (value) => (value && isValidCPF(onlyNumbers(value))) || false,
    ),
  cbo: Yup.string().required(required),
  rqe: Yup.string(),
  council_name: Yup.string().required(required),
  council_region: Yup.string().required(required),
  council_number: Yup.string().required(required),
});

export const clinicValidationSchema = Yup.object().shape({
  zip_code: Yup.string()
    .required(required)
    .matches(/^\d{5}-\d{3}$/, 'Insira um CEP válido'),
  clinic_cnpj: Yup.string().matches(
    /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
    'Insira um CNPJ válido',
  ),
  address: Yup.string().required(required),
  number: Yup.string().required(required),
  complement: Yup.string(),
  neighborhood: Yup.string().required(required),
  city: Yup.string().required(required),
  state: Yup.string().required(required),
  clinic_as_billing: Yup.boolean(),
});

export const createProfessionalValidation = (cboList: CBO[]) =>
  professionalValidationSchema.concat(
    Yup.object().shape({
      cbo: Yup.string()
        .required(required)
        .test(
          'check-cbo',
          'Escolha uma especialidade da lista',
          (value) => !!cboList.find((item) => item.term === value),
        ),
    }),
  );

const cfmUpdateFields = Yup.object({
  person_kind: Yup.string()
    .oneOf([PERSON_KIND.PF, PERSON_KIND.PJ], 'Escolha entre PF ou PJ')
    .required(),
});

export const validationClinicBillingForm = cfmUpdateFields
  .concat(
    Yup.object({
      responsible: Yup.string()
        .max(120, maxLimitMessage(120))
        .required('Campo obrigatório')
        .test(
          'has-two-names',
          'Informe nome e sobrenome ou razão social composta',
          (value) => !!value && value.trim().split(/\s+/).length >= 2,
        ),
      cpf_cnpj: Yup.string().when('person_kind', {
        is: PERSON_KIND.PJ,
        then: Yup.string()
          .required('CNPJ obrigatório')
          .test('is-valid-cnpj', 'CNPJ inválido', (value) =>
            isValidCNPJ(onlyNumbers(value ?? '')),
          ),
        otherwise: Yup.string()
          .required('CPF obrigatório')
          .test('is-valid-cpf', 'CPF inválido', (value) =>
            isValidCPF(onlyNumbers(value ?? '')),
          ),
      }),
      billing_birthdate: Yup.string().when('person_kind', {
        is: PERSON_KIND.PF,
        then: Yup.string()
          .required('Data de Nascimento obrigatória')
          .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato deve ser DD/MM/AAAA')
          .test('is-valid-date', 'Data inválida', isValidDate)
          .test('is-18', 'Deve ser maior de 18 anos', isOver18)
          .test(
            'not-today-or-future',
            'Data de Nascimento inválida',
            isNotTodayOrFuture,
          ),
      }),
    }),
  )
  .concat(
    Yup.object(
      Object.fromEntries(
        Object.entries(validationAddressForm.fields).filter(
          ([key]) =>
            ![
              'person_type',
              'responsible',
              'cpf_cnpj',
              'billing_birthdate',
            ].includes(key),
        ),
      ),
    ),
  );
